import React, {
  MouseEvent,
  useMemo,
  useEffect,
  useRef,
  FocusEventHandler,
  HTMLAttributes,
  useState,
  useCallback,
  SyntheticEvent,
  Key,
  RefObject,
} from 'react';
import defer from 'lodash/defer';
import {
  TemplateListItemDTO,
  TemplateListItemDTOPaginatedResponseDTO,
} from 'dtos';
import AutoCompletedInput from 'core/components/AutoComplete';
import IconButton from '@mui/material/IconButton';
import Image from 'pages/Dashboard/components/Image';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import DeleteIcon from 'core/icons/delete';
import {
  useConfirmDialog,
} from 'core/hooks';
import {
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import {
  useTemplateHooks,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import ListboxDropdown from 'views/EMR/components/ListboxDropdown';
import DefaultOptionContainer from 'core/components/Lookup/Generic/DefaultOptionContainer';
import clsx from 'clsx';
import Spinner from 'pages/Dashboard/components/Spinner';
import Checkbox from '@mui/material/Checkbox';
import Input from 'pages/Dashboard/components/Input';
import SearchIcon from 'core/icons/search';
import Skeleton from 'core/components/Skeleton';
import Button from 'pages/Dashboard/components/Button';
import useDebounce from 'pages/Dashboard/hooks/useDebounce';

export function TemplateListSkeleton() {
  return (
    <div role="status" aria-label="Loading templates" className="w-full animate-pulse">
      {Array.from({ length: 8 }, (_, index) => (
        <div
          key={index}
          className="flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2"
        >
          <Skeleton variant="rounded" className="!h-4 !w-4 shrink-0 !rounded" />
          <div className="min-w-0 flex-1">
            <Skeleton variant="rounded" className="!h-4 !w-full !rounded" />
          </div>
          <Skeleton className="!h-5 !w-5 shrink-0" />
        </div>
      ))}
    </div>
  );
}

type Props = {
  onChange?: (value: TemplateListItemDTO) => void;
  onMultipleChange?: (value: TemplateListItemDTO[]) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  value?: TemplateListItemDTO;
  selectedTemplates?: TemplateListItemDTO[];
  multiple?: boolean;
  inline?: boolean;
  section?: string;
  filterOptions?: (options: TemplateListItemDTO[]) => TemplateListItemDTO[];
};

type ItemProps = HTMLAttributes<HTMLLIElement> & {
  template: TemplateListItemDTO;
  selected: boolean;
  showCheckbox: boolean;
  flat?: boolean;
};

export function TemplateOption({
  template,
  selected,
  showCheckbox,
  flat = false,
  ...props
}: ItemProps) {
  const { key, ...optionProps } = props as ItemProps & { key?: Key };
  const userInfo = useUserInfo();
  const { confirm, closeConfirm } = useConfirmDialog();
  const {
    templateName,
    user,
    userId,
  } = template;

  const allowDelete = useMemo(
    () => userId === userInfo?.user?.userId,
    [userId, userInfo?.user?.userId],
  );

  const handleDeleteClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    confirm({
      title: 'Delete Template',
      content: (
        <p>
          Are you sure you would like to delete
          {' '}
          <strong>{templateName}</strong>
          ?
        </p>
      ),
      onConfirm: () => {
        visitNoteEventChannel.emit('onTemplateDelete', template);
        closeConfirm();
      },
    });
  };

  return (
    <DefaultOptionContainer
      key={key}
      {...optionProps}
      id={optionProps.id ?? templateName ?? ''}
      className={clsx('flex items-center !justify-between cursor-pointer hover:bg-blue-50', optionProps.className, {
        'rounded p-1.5': !flat,
        'border-b border-gray-100 px-3 py-1.5 last:border-b-0': flat,
        '!bg-blue-100': selected,
      })}
    >
      <span className="flex min-w-0 items-center">
        {showCheckbox && (
          <Checkbox
            checked={selected}
            className="!-ml-1 !mr-1 !p-1"
            size="small"
            tabIndex={-1}
            disableRipple
            slotProps={{ input: { 'aria-label': templateName ?? 'Template' } }}
          />
        )}
        <span className="truncate text-xs">{templateName}</span>
      </span>
      {allowDelete
        ? (
          <IconButton
            className="!p-0 nw-delete-template-button"
            aria-label="Delete Template"
            size="small"
            onClick={handleDeleteClick}
          >
            <DeleteIcon className="!text-xl text-gray-500" />
          </IconButton>
        )
        : (
          <Image
            size="xs"
            variant="circular"
            alt={getDisplayName(user?.formattedNames)}
          />
        )}
    </DefaultOptionContainer>
  );
}

type InlineTemplateMultiSelectProps = {
  options: TemplateListItemDTO[];
  selectedTemplates: TemplateListItemDTO[];
  isLoading: boolean;
  searchValue: string;
  inputRef: RefObject<HTMLInputElement | null>;
  onSearchChange: (value: string) => void;
  onChange?: (value: TemplateListItemDTO[]) => void;
  hasNextPage: boolean;
  onLoadMore: () => void;
  isFetching: boolean;
};

function InlineTemplateMultiSelect({
  options,
  selectedTemplates,
  isLoading,
  searchValue,
  inputRef,
  onSearchChange,
  onChange,
  hasNextPage,
  onLoadMore,
  isFetching,
}: InlineTemplateMultiSelectProps) {
  const renderOption = (option: TemplateListItemDTO) => {
    const isSelected = selectedTemplates.some((template) => (
      template.templateName === option.templateName && template.sectionName === option.sectionName
    ));
    return (
      <TemplateOption
        key={`${option.sectionName}:${option.templateName}`}
        template={option}
        selected={isSelected}
        showCheckbox
        flat
        role="option"
        aria-selected={isSelected}
        tabIndex={0}
        onClick={() => {
          const nextValue = isSelected
            ? selectedTemplates.filter((template) => (
              template.templateName !== option.templateName || template.sectionName !== option.sectionName
            ))
            : [...selectedTemplates, option];
          onChange?.(nextValue);
        }}
        onKeyDown={(event) => {
          if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            event.currentTarget.click();
          }
        }}
      />
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200">
      <div className="border-b border-gray-200 p-2">
        <Input
          id="template-name"
          value={searchValue}
          placeholder="Search for Template"
          icon={<SearchIcon className="!ml-3 !h-4 !w-4 text-gray-400" />}
          inputRef={inputRef}
          onChange={onSearchChange}
        />
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        {isLoading && (
          <TemplateListSkeleton />
        )}
        {!isLoading && options.length === 0 && (
          <div className="flex h-full min-h-32 items-center justify-center text-xs text-gray-500">
            No templates found
          </div>
        )}
        {!isLoading && (
          <div className="min-h-0 flex-1 overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>
            {options.map(renderOption)}
          </div>
        )}
        {hasNextPage && (
          <Button
            variant="text"
            className="!w-full !text-xs"
            disabled={isFetching}
            onClick={onLoadMore}
          >
            Load more templates
          </Button>
        )}
      </div>
    </div>
  );
}

export default function TemplateSelect({
  onChange,
  onMultipleChange,
  selectedTemplates,
  multiple = false,
  inline = false,
  section,
  filterOptions,
  ...props
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    defer(() => {
      inputRef?.current?.focus?.();
    });
  }, []);

  const { useGetList } = useTemplateHooks(section);
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const debouncedSectionSearch = useDebounce(searchValue, 800);
  const searchQuery = section ? debouncedSectionSearch.trim() : inputValue;
  const [page, setPage] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    setTimeout(() => {
      setOpen(true);
    }, 100);
  }, []);

  const query = useGetList({
    searchQuery,
    sectionName: section ?? '',
    ...(!section ? { pagination: { pageIndex: 0, pageSize: (page + 1) * 500, page: 0 } } : {}),
  });
  const { data, isFetching } = query;
  const isLoading = section ? query.isPending : isFetching;

  const {
    data: options = [],
    pageProperties,
  } = (data ?? {}) as TemplateListItemDTOPaginatedResponseDTO;

  const onInputChange = useCallback(debounce((
    event: SyntheticEvent,
    value: string,
  ) => {
    if (!isNil(event)) {
      setInputValue(value);
      setPage(0);
    }
  }, 700), []);

  const fetchNextPage = useCallback(() => {
    if (pageProperties?.hasNextPage && !isFetching) {
      if (query.fetchNextPage) {
        query.fetchNextPage({ cancelRefetch: false });
      } else {
        setPage((current) => current + 1);
      }
    }
  }, [isFetching, pageProperties, query]);

  const dropdownId = 'template-name';

  const noOptionsText = useMemo(() => (
    (options ?? []).length === 0 && searchQuery === ''
      ? 'Start typing to see the list'
      : 'No result Found'
  ), [options, searchQuery]);

  return inline && multiple ? (
      <InlineTemplateMultiSelect
        options={options ?? []}
        selectedTemplates={selectedTemplates ?? []}
        isLoading={isLoading}
        searchValue={searchValue}
        inputRef={inputRef}
        onSearchChange={(value) => {
          setSearchValue(value);
          if (!section) onInputChange({} as SyntheticEvent, value);
        }}
        onChange={onMultipleChange}
        hasNextPage={pageProperties?.hasNextPage ?? false}
        onLoadMore={fetchNextPage}
        isFetching={isFetching}
      />
  ) : (
    <AutoCompletedInput<TemplateListItemDTO>
      {...props}
      options={(options ?? [])}
      onChange={(value) => {
        if (multiple) {
          onMultipleChange?.(value as TemplateListItemDTO[]);
        } else {
          onChange?.(value as TemplateListItemDTO);
          setOpen(false);
        }
      }}
      value={multiple ? selectedTemplates : props.value}
      multiple={multiple}
      disableCloseOnSelect={multiple}
      open={open}
      onOpen={() => { setOpen(true); }}
      onClose={() => {
        setOpen(false);
      }}
      id="template-name"
      noOptionsText={isLoading ? 'Loading...' : noOptionsText}
      placeholder="Search for Template"
      dropdownClassName="p-2 template-select"
      inputRef={inputRef}
      onInputChange={section ? (event, value) => {
        if (!isNil(event)) setSearchValue(value);
      } : onInputChange}
      filterSelectedOptions={false}
      filterOptions={(x) => x}
      renderValue={multiple ? () => null : undefined}
      renderOption={(itemProps, option: TemplateListItemDTO, { selected }) => (
        <TemplateOption
          {...itemProps}
          key={option.templateName}
          template={option}
          selected={selected}
          showCheckbox={multiple}
        />
      )}
      loading={isLoading}
      loadingText="Loading..."
      getLabel={(option: TemplateListItemDTO) => option.templateName as string}
      isOptionEqualToValue={(a, b) => (
        a?.templateName === (b as TemplateListItemDTO)?.templateName
        && (!section || a.sectionName === (b as TemplateListItemDTO)?.sectionName)
      )}
      popupIcon={isLoading ? <Spinner /> : undefined}
      slotProps={{
        listbox: {
          component: ListboxDropdown,
          pageProperties: isNil(pageProperties) ? null : pageProperties,
          fetchNextPage,
          isLoading,
          id: dropdownId,
          count: options?.length,
          className: '!max-h-48',
          isLoadingMore: section ? isFetching : isLoading,
        },
      } as Object}
    />
  );
}
